import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { subcontractorsApi, specialtiesApi } from "../../lib/apiSubcontractors";
import type {
  Subcontractor,
  SubcontractorFilters,
} from "../../types/subcontractors";
import { ResponsiveTable } from "../common/ResponsiveTable";
import { SavedBillingViews } from "../billing/BillingWorkflowTools";
import { useAuth } from "../../contexts/AuthContext";

export const SubcontractorList: React.FC<{
  onSelectSubcontractor?: (s: Subcontractor) => void;
  hideActions?: boolean;
}> = ({ onSelectSubcontractor, hideActions = false }) => {
  const navigate = useNavigate(),
    client = useQueryClient(),
    { user } = useAuth();
  const [params, setParams] = useSearchParams(),
    [draft, setDraft] = useState(params.get("search") || "");
  const page = Math.max(1, Number(params.get("page")) || 1),
    scope = params.toString();
  const [selection, setSelection] = useState<{ scope: string; ids: number[] }>({
    scope: "",
    ids: [],
  });
  const selected = selection.scope === scope ? selection.ids : [];
  const filters: SubcontractorFilters = {
    search: params.get("search") || undefined,
    status: params.get("status") || undefined,
    city: params.get("city") || undefined,
    state: params.get("state") || undefined,
    compliance: params.get("compliance") || undefined,
    specialty: params.get("specialty")
      ? [Number(params.get("specialty"))]
      : undefined,
  };
  const list = useQuery({
    queryKey: ["subcontractors", user?.organization, filters, page],
    queryFn: () => subcontractorsApi.list(filters, page),
  });
  const specialties = useQuery({
    queryKey: ["subcontractor-specialties"],
    queryFn: () => specialtiesApi.list(),
  });
  const archive = useMutation({
    mutationFn: (ids: number[]) => subcontractorsApi.bulkArchive(ids),
    onSuccess: () => {
      setSelection({ scope: "", ids: [] });
      client.invalidateQueries({ queryKey: ["subcontractors"] });
    },
  });
  const change = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    next.delete("page");
    setSelection({ scope: "", ids: [] });
    setParams(next);
  };
  const open = (s: Subcontractor) =>
    onSelectSubcontractor
      ? onSelectSubcontractor(s)
      : navigate(`/operations/subcontractors/${s.id}`);
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        sx={{ gap: 1, flexWrap: "wrap", justifyContent: "space-between" }}
      >
        <Typography level="h3">Subcontractor directory</Typography>
        {!hideActions && (
          <Button onClick={() => navigate("/operations/subcontractors/new")}>
            Add subcontractor
          </Button>
        )}
      </Stack>
      <Card>
        <Typography level="title-lg">Compliance renewal queues</Typography>
        <Typography level="body-sm">
          Review missing paperwork, expiry dates and renewals due within 30
          days.
        </Typography>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
          {["missing", "expiring", "expired", "ready"].map((q) => (
            <Button
              key={q}
              variant={filters.compliance === q ? "solid" : "soft"}
              onClick={() => change("compliance", q)}
            >
              {q[0].toUpperCase() + q.slice(1)}
            </Button>
          ))}
          <Button variant="plain" onClick={() => change("compliance", "")}>
            All compliance states
          </Button>
        </Stack>
      </Card>
      <SavedBillingViews kind="subcontractors" />
      <CollapsibleFilters><Card>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
          <Input
            placeholder="Search company, contact or notes"
            aria-label="Search subcontractors"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && change("search", draft.trim())
            }
          />
          <Button onClick={() => change("search", draft.trim())}>Search</Button>
          <Button
            variant="plain"
            onClick={() => {
              setDraft("");
              setSelection({ scope: "", ids: [] });
              setParams({});
            }}
          >
            Clear filters
          </Button>
        </Stack>
        <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Select
              value={filters.status || ""}
              onChange={(_, v) => change("status", v || "")}
            >
              <Option value="">All</Option>
              {["active", "pending", "suspended", "inactive"].map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Trade</FormLabel>
            <Select
              value={filters.specialty?.[0] || 0}
              onChange={(_, v) => change("specialty", v ? String(v) : "")}
            >
              <Option value={0}>All trades</Option>
              {specialties.data?.map((s) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Service city</FormLabel>
            <Input
              value={filters.city || ""}
              onChange={(e) => change("city", e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>State</FormLabel>
            <Input
              value={filters.state || ""}
              onChange={(e) => change("state", e.target.value)}
              slotProps={{ input: { maxLength: 2 } }}
            />
          </FormControl>
        </Stack>
        <Typography level="body-sm">
          {list.data?.count ?? 0} matches · Search: {filters.search || "all"}.
          Location uses the profile's business address.
        </Typography>
      </Card></CollapsibleFilters>
      {list.isError && (
        <Alert color="danger">
          Unable to load subcontractors.{" "}
          <Button onClick={() => list.refetch()}>Retry</Button>
        </Alert>
      )}
      {archive.isError && (
        <Alert color="danger">Unable to archive. Please retry.</Alert>
      )}
      {selected.length > 0 && (
        <Button
          color="warning"
          loading={archive.isPending}
          onClick={() =>
            window.confirm(
              `Archive ${selected.length} selected subcontractors?`,
            ) && archive.mutate(selected)
          }
        >
          Archive {selected.length} selected
        </Button>
      )}
      {list.isLoading ? (
        <Typography>Loading subcontractors…</Typography>
      ) : (
        <ResponsiveTable<Subcontractor>
          rows={list.data?.results || []}
          getRowKey={(s) => s.id}
          pagination={{
            page,
            pageSize: 20,
            totalCount: list.data?.count || 0,
            onPageChange: (p) => {
              const next = new URLSearchParams(params);
              next.set("page", String(p));
              setSelection({ scope: "", ids: [] });
              setParams(next);
            },
            itemNoun: "subcontractors",
          }}
          columns={[
            ...(!hideActions
              ? [
                  {
                    key: "select",
                    header: "Select",
                    render: (s: Subcontractor) => (
                      <Checkbox
                        slotProps={{
                          input: { "aria-label": `Select ${s.company_name}` },
                        }}
                        checked={selected.includes(s.id)}
                        onChange={(e) =>
                          setSelection({
                            scope,
                            ids: e.target.checked
                              ? [...selected, s.id]
                              : selected.filter((id) => id !== s.id),
                          })
                        }
                      />
                    ),
                  },
                ]
              : []),
            {
              key: "company",
              header: "Company",
              mobileVariant: "header",
              render: (s) => (
                <Button variant="plain" onClick={() => open(s)}>
                  {s.company_name}
                </Button>
              ),
            },
            {
              key: "trade",
              header: "Trade / area",
              render: (s) => (
                <Box>
                  <Typography>
                    {s.specialties.map((v) => v.name).join(", ") ||
                      "No trades listed"}
                  </Typography>
                  <Typography level="body-sm">
                    {[s.city, s.state].filter(Boolean).join(", ") ||
                      "No area listed"}
                  </Typography>
                </Box>
              ),
            },
            {
              key: "status",
              header: "Status / workload",
              render: (s) => (
                <Stack spacing={1}>
                  <Chip size="sm">{s.status}</Chip>
                  <Typography level="body-sm">
                    {s.active_work_orders || 0} active orders · Confirm
                    availability
                  </Typography>
                </Stack>
              ),
            },
            {
              key: "compliance",
              header: "Compliance",
              render: (s) => (
                <Box>
                  <Chip
                    size="sm"
                    color={
                      s.compliance?.state === "ready" ? "success" : "warning"
                    }
                  >
                    {s.compliance?.state || "Review needed"}
                  </Chip>
                  <Typography level="body-xs">
                    {[
                      ...(s.compliance?.missing || []),
                      ...(s.compliance?.expired || []),
                    ].join(", ")}
                  </Typography>
                </Box>
              ),
            },
            {
              key: "notes",
              header: "Qualification notes",
              mobileVariant: "body",
              render: (s) => (
                <Typography
                  sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                >
                  {s.notes || "No notes recorded"}
                </Typography>
              ),
            },
          ]}
          rowActions={(s) => (
            <Button size="sm" variant="outlined" onClick={() => open(s)}>
              Open profile
            </Button>
          )}
        />
      )}
    </Stack>
  );
};
